/*
 * variant-swap-instant.js
 *
 * Makes size/colour selection feel instant on both the PDP and the
 * Quick View drawer. Shopify's default variant-selects handler
 * round-trips to the server (~300–900ms) for a fresh section render on
 * every click — the user visibly waits for the "Size: M" label and the
 * cart variant id to update.
 *
 * This script does the perceptual work locally the moment the radio
 * flips. The server fetch still runs in parallel as the source of
 * truth for price, media, and availability, but the user no longer
 * waits for it to see their choice reflected.
 *
 * Scope is any product-info ancestor (works on PDP and inside
 * .product-quick-view). Quick Add cards on collection pages use a
 * different DOM and are excluded.
 *
 * Local updates:
 *   - Label value text in the .variant-label-container for the
 *     changed option (e.g. "Size: M" -> "Size: L")
 *   - Hidden <input name="id"> in the product form so Add-to-Cart
 *     targets the newly chosen variant without waiting
 *   - Desktop sticky ATC bar's .selected-variant-option row for the
 *     matching option name (e.g. "Size: M" -> "Size: L"). Matches the
 *     radio's name attribute ("Size-1") against the sticky row's
 *     data-option-name ("Size"). Only present on desktop PDP.
 *
 * Variants JSON lookup (in priority order):
 *   1. script[id^="ProductVariants-"]     (embedded in main-product.liquid)
 *   2. script[id^="QuickViewVariants-"]   (embedded in main-product-quick-view.liquid)
 *   3. script[id^="sticky-variants-"]     (already on page when sticky ATC is enabled)
 */
(function () {
  'use strict';

  if (window.__variantSwapInstantAttached) return;
  window.__variantSwapInstantAttached = true;

  var VARIANTS_SCRIPT_SELECTOR =
    'script[id^="ProductVariants-"], script[id^="QuickViewVariants-"], script[id^="sticky-variants-"]';

  function findVariants(scope) {
    var script = scope.querySelector(VARIANTS_SCRIPT_SELECTOR);
    if (!script) script = document.querySelector(VARIANTS_SCRIPT_SELECTOR);
    if (!script) return null;
    try {
      var parsed = JSON.parse(script.textContent);
      return Array.isArray(parsed) ? parsed : null;
    } catch (e) {
      return null;
    }
  }

  function collectSelectedValues(scope) {
    var values = [];
    scope.querySelectorAll('.product-form__input').forEach(function (fs) {
      if (fs.classList.contains('product-form__quantity')) return;
      var checked = fs.querySelector('input[type="radio"]:checked');
      if (checked) {
        values.push(checked.value);
        return;
      }
      var select = fs.querySelector('select');
      if (select) values.push(select.value);
    });
    return values;
  }

  /*
   * Match by membership, not by index. Variant JSON has option1/2/3
   * ordered by the product's defined option positions (e.g. Size=option1,
   * Colour=option2). The Quick View DOM can render fieldsets in a
   * different order (e.g. Colour above Size), so a positional compare
   * fails. Instead we check that the variant's non-null options are the
   * same set as the selected values. This is robust as long as the same
   * literal value isn't reused across two different options on the same
   * product (the standard Shopify case).
   */
  function findVariant(variants, selectedValues) {
    return variants.find(function (v) {
      var vOpts = [v.option1, v.option2, v.option3].filter(function (o) {
        return o !== null && o !== undefined;
      });
      if (vOpts.length !== selectedValues.length) return false;
      return selectedValues.every(function (val) {
        return vOpts.indexOf(val) !== -1;
      });
    });
  }

  function updateLabelValue(fieldset, newValue) {
    var container = fieldset.querySelector('.variant-label-container');
    if (!container) return;
    var valueSpan = container.querySelector('.form__label-value');
    if (valueSpan) valueSpan.textContent = newValue;
  }

  function updateVariantInput(scope, variantId) {
    var input = scope.querySelector(
      'product-form input[name="id"], form[action*="/cart/add"] input[name="id"]'
    );
    if (input) input.value = variantId;
  }

  /*
   * Desktop sticky ATC bar lives outside <product-info> (it's a sibling
   * <product-add-to-cart-sticky>) and renders each option as:
   *   <div class="selected-variant-option" data-option-name="Size">
   *     Size: M
   *   </div>
   * Its own handleVariantChange only runs after the server round-trip,
   * so the "Size: M" text lags ~300–900ms behind the label the user
   * just updated on the main picker. Mirror the change locally.
   *
   * The radio's name attribute is "{OptionName}-{position}" (e.g.
   * "Size-1"). We strip the trailing -N and match against the row's
   * data-option-name.
   */
  function updateStickyOptionLabel(input, newValue) {
    var name = input.name || '';
    var dashIdx = name.lastIndexOf('-');
    if (dashIdx < 1) return;
    var optionName = name.substring(0, dashIdx);
    document
      .querySelectorAll('.selected-variant-option[data-option-name]')
      .forEach(function (el) {
        if (el.dataset.optionName === optionName) {
          el.textContent = optionName + ': ' + newValue;
        }
      });
  }

  function handleChange(e) {
    var input = e.target;
    if (!input || !input.matches || !input.matches('.product-form__input input[type="radio"]')) return;
    var scope = input.closest('product-info, .product-quick-view');
    if (!scope) return;
    var fieldset = input.closest('.product-form__input');
    if (!fieldset) return;

    updateLabelValue(fieldset, input.value);
    updateStickyOptionLabel(input, input.value);

    var variants = findVariants(scope);
    if (!variants) return;
    var variant = findVariant(variants, collectSelectedValues(scope));
    if (variant) updateVariantInput(scope, variant.id);
  }

  document.addEventListener('change', handleChange, true);
})();